import React from 'react';
import { AvailableCt, ALL_ACTIONS, FIELD_ACTIONS, ACTION_PREFIX, ActionShort } from './roleFormUtils';

interface ContentTypesTabProps {
  loading: boolean;
  contentTypes: AvailableCt[];
  isEnabled: (action: string, subject: string | null) => boolean;
  togglePerm: (action: string, subject: string | null) => void;
  toggleAllActionsForCt: (subject: string, actions: ActionShort[]) => void;
  toggleField: (action: string, subject: string, field: string) => void;
  enabledFields: Map<string, string[]>;
  expandedCt: string | null;
  setExpandedCt: (subject: string | null) => void;
  selectAllContent: () => void;
  deselectAllContent: () => void;
  isSystem?: boolean;
}

const ContentTypesTab: React.FC<ContentTypesTabProps> = ({
  loading,
  contentTypes,
  isEnabled,
  togglePerm,
  toggleAllActionsForCt,
  toggleField,
  enabledFields,
  expandedCt,
  setExpandedCt,
  selectAllContent,
  deselectAllContent,
  isSystem,
}) => {
  return (
    <div className="border border-t-0 border-gray-200 rounded-b-lg">
      <div className="flex justify-end gap-2 p-3 bg-gray-50 border-b border-gray-200">
        <button type="button" onClick={selectAllContent} className="text-xs text-ocean-600 hover:text-ocean-800 font-medium" disabled={isSystem}>
          Select All
        </button>
        <span className="text-gray-300">|</span>
        <button type="button" onClick={deselectAllContent} className="text-xs text-red-600 hover:text-red-800 font-medium" disabled={isSystem}>
          Deselect All
        </button>
      </div>

      {loading && (
        <div className="p-8 text-center text-gray-500">Loading available permissions...</div>
      )}

      {!loading && contentTypes.length === 0 && (
        <div className="p-8 text-center text-gray-500">No content types found.</div>
      )}

      {!loading && contentTypes.length > 0 && (
        <div className="max-h-[28rem] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="py-2 px-4 text-left text-xs font-semibold text-gray-600 uppercase w-1/4">Content Type</th>
                {ALL_ACTIONS.map(a => (
                  <th key={a} className="py-2 px-2 text-center text-xs font-semibold text-gray-600 uppercase w-16">{a}</th>
                ))}
                <th className="py-2 px-2 text-center text-xs font-semibold text-gray-600 uppercase w-20">Fields</th>
              </tr>
            </thead>
            <tbody>
              {contentTypes.map(ct => {
                const allOn = ct.availableActions.every(a => isEnabled(`${ACTION_PREFIX}${a}`, ct.subject));
                const someOn = ct.availableActions.some(a => isEnabled(`${ACTION_PREFIX}${a}`, ct.subject));
                const isExpanded = expandedCt === ct.subject;

                const allFieldNames = new Set<string>();
                ct.fieldsByAction.forEach(fields => fields.forEach(f => allFieldNames.add(f)));
                const fieldCount = allFieldNames.size;

                return (
                  <React.Fragment key={ct.subject}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={allOn}
                            ref={el => { if (el) el.indeterminate = someOn && !allOn; }}
                            onChange={() => toggleAllActionsForCt(ct.subject, ct.availableActions)}
                            className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded mr-3"
                            disabled={isSystem}
                          />
                          <span className="text-sm font-medium text-gray-900">{ct.label}</span>
                        </div>
                      </td>
                      {ALL_ACTIONS.map(action => {
                        const available = ct.availableActions.includes(action);
                        return (
                          <td key={action} className="py-3 px-2 text-center">
                            {available ? (
                              <input
                                type="checkbox"
                                checked={isEnabled(`${ACTION_PREFIX}${action}`, ct.subject)}
                                onChange={() => togglePerm(`${ACTION_PREFIX}${action}`, ct.subject)}
                                className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
                                disabled={isSystem}
                              />
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="py-3 px-2 text-center">
                        {fieldCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => setExpandedCt(isExpanded ? null : ct.subject)}
                            className="text-xs text-ocean-600 hover:text-ocean-800 font-medium"
                          >
                            {fieldCount} {isExpanded ? '\u25B2' : '\u25BC'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && fieldCount > 0 && (
                      <tr className="bg-blue-50">
                        <td colSpan={ALL_ACTIONS.length + 2} className="px-4 py-3">
                          <div className="text-xs font-semibold text-gray-600 mb-2">
                            Fields/Columns for {ct.label}:
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-blue-200">
                                  <th className="py-1 px-2 text-left font-semibold text-gray-600">Field</th>
                                  {FIELD_ACTIONS.map(a => (
                                    <th key={a} className="py-1 px-2 text-center font-semibold text-gray-600 w-16">
                                      {a.charAt(0).toUpperCase() + a.slice(1)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {Array.from(allFieldNames).sort().map(field => (
                                  <tr key={field} className="border-b border-blue-100 hover:bg-blue-100">
                                    <td className="py-1 px-2 font-mono text-gray-700">{field}</td>
                                    {FIELD_ACTIONS.map(action => {
                                      const actionEnabled = isEnabled(`${ACTION_PREFIX}${action}`, ct.subject);
                                      const actionAvailable = ct.availableActions.includes(action);
                                      if (!actionAvailable || !actionEnabled) {
                                        return <td key={action} className="py-1 px-2 text-center"><span className="text-gray-300">-</span></td>;
                                      }
                                      const k = `${ACTION_PREFIX}${action}||${ct.subject}`;
                                      const currentFields = enabledFields.get(k) || [];
                                      return (
                                        <td key={action} className="py-1 px-2 text-center">
                                          <input
                                            type="checkbox"
                                            checked={currentFields.includes(field)}
                                            onChange={() => toggleField(`${ACTION_PREFIX}${action}`, ct.subject, field)}
                                            className="h-3 w-3 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
                                            disabled={isSystem}
                                          />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContentTypesTab;
